import {useState, useCallback} from 'react';
import {
    fetchInvoiceTransferHistory,
    type GlInvoiceTransferHistoryVO,
    type GlSheetRowVO,
} from '@/services/accountService/glInvoiceService';

export interface GlTransferInvoicePayload {
    invoiceId: string;
    date: string | null;
    description: string | null;
    /** 当前 Tab 上该发票的金额（带符号） */
    amount?: number | null;
    columnValues?: Record<string, number>;
    /** 当前 Tiny Tab 源科目 */
    sourceFsCode?: string | null;
    sourceGlCode?: string | null;
    sourceLabel?: string | null;
}

/**
 * Tiny Mode 发票转移抽屉状态：打开时拉取转移历史（单票时按票；多票时不预拉）。
 */
export function useGlInvoiceTransferDrawer(companyId?: number, annualId?: number) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<GlInvoiceTransferHistoryVO[]>([]);
    const [invoices, setInvoices] = useState<GlTransferInvoicePayload[]>([]);

    const refreshHistory = useCallback(async (invoiceId: string) => {
        if (companyId == null || annualId == null || !invoiceId) {
            setHistory([]);
            return;
        }
        setLoading(true);
        try {
            const list = await fetchInvoiceTransferHistory(companyId, annualId, invoiceId);
            setHistory(Array.isArray(list) ? list : []);
        } catch (e) {
            console.error('[useGlInvoiceTransferDrawer] Failed to fetch transfer history:', e);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId]);

    const handleOpen = useCallback(async (payload: GlTransferInvoicePayload | GlTransferInvoicePayload[]) => {
        const list = (Array.isArray(payload) ? payload : [payload]).filter(
            (p) => p?.invoiceId && !p.invoiceId.startsWith('__prior_bf__'),
        );
        if (list.length === 0) return;
        setInvoices(list);
        setHistory([]);
        setOpen(true);
        if (list.length === 1) {
            await refreshHistory(list[0].invoiceId);
        }
    }, [refreshHistory]);

    const handleOpenFromSheetRow = useCallback(async (
        row: GlSheetRowVO,
        amount?: number | null,
        source?: {fsCode?: string; glCode?: string; label?: string},
    ) => {
        await handleOpen({
            invoiceId: row.invoiceId,
            date: row.date,
            description: row.description,
            amount: amount ?? null,
            columnValues: row.columnValues,
            sourceFsCode: source?.fsCode ?? null,
            sourceGlCode: source?.glCode ?? null,
            sourceLabel: source?.label ?? null,
        });
    }, [handleOpen]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setInvoices([]);
        setHistory([]);
    }, []);

    return {
        open,
        loading,
        history,
        /** 当前打开的发票列表（单条或多条） */
        invoices,
        /** @deprecated 使用 invoices[0]；保留兼容单票调用方 */
        invoice: invoices[0] ?? null,
        handleOpen,
        handleOpenFromSheetRow,
        handleClose,
        refreshHistory,
        setHistory,
    };
}
